import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_META = {
  registration: { emoji: "📝", color: "bg-neutral-500/15 border-neutral-500/40 text-neutral-300" },
  training: { emoji: "⚽", color: "bg-sky-500/15 border-sky-500/40 text-sky-300" },
  video: { emoji: "🎬", color: "bg-purple-500/15 border-purple-500/40 text-purple-300" },
  coach_session: { emoji: "💬", color: "bg-[#e8559a]/15 border-[#e8559a]/40 text-[#e8559a]" },
};

/**
 * DevelopmentTimeline — chronological view of player development events.
 * Read-only, parent-facing. Fetches from /api/p/{token}/development.
 */
export default function DevelopmentTimeline({ token }) {
  const { t } = useLang();
  const [data, setData] = useState({ events: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/p/${token}/development`).then((r) => {
      if (alive) {
        setData(r.data);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [token]);

  if (loading) {
    return <div className="text-neutral-500 p-10 text-center">...</div>;
  }

  const events = data.events || [];

  if (events.length === 0) {
    return (
      <div className="rounded-2xl bg-[#161616] border border-[#252525] p-10 text-center text-neutral-500">
        {t("dev_timeline_empty")}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dev-timeline">
      <h3 className="font-bebas text-2xl tracking-widest text-white">
        📈 {t("dev_timeline")}
      </h3>
      <div className="relative pl-6 sm:pl-8">
        {/* Vertical line */}
        <div className="absolute left-2 sm:left-3 top-2 bottom-2 w-px bg-gradient-to-b from-[#e8559a]/60 via-[#252525] to-transparent" />

        <div className="space-y-4">
          {events.map((ev, i) => {
            const meta = EVENT_META[ev.type] || EVENT_META.training;
            return (
              <div key={i} className="relative">
                {/* Dot */}
                <div
                  className={`absolute -left-6 sm:-left-8 top-3 w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 ${meta.color} flex items-center justify-center text-[10px]`}
                >
                  {meta.emoji}
                </div>
                <div
                  data-testid={`timeline-event-${i}`}
                  className="rounded-xl bg-[#161616] border border-[#252525] p-4"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1">
                        {ev.date || "—"}
                      </div>
                      <div className="text-white font-medium">
                        {ev.title}
                      </div>
                      {ev.description && (
                        <div className="text-xs text-neutral-400 mt-1">
                          {ev.description}
                        </div>
                      )}
                      {ev.type === "coach_session" && (
                        <CoachSessionDetails ev={ev} t={t} />
                      )}
                    </div>
                    {ev.type === "training" && ev.attendance && (
                      <span className="flex-shrink-0 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400">
                        ✓ {ev.attendance}
                      </span>
                    )}
                    {ev.type === "video" && ev.category && (
                      <span className="flex-shrink-0 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-purple-500/40 bg-purple-500/10 text-purple-300">
                        {ev.category.replace("progress_", "")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CoachSessionDetails({ ev, t }) {
  const items = [
    { emoji: "🎯", label: t("dev_objective"), text: ev.objective, color: "text-neutral-300" },
    { emoji: "✅", label: t("dev_what_improved"), text: ev.what_improved, color: "text-emerald-400" },
    { emoji: "🎯", label: t("dev_what_needs_work"), text: ev.what_needs_work, color: "text-amber-300" },
    { emoji: "📚", label: t("dev_homework"), text: ev.homework, color: "text-sky-300" },
    { emoji: "🚀", label: t("dev_next_objective"), text: ev.next_objective, color: "text-[#e8559a]" },
  ].filter((i) => i.text);

  if (items.length === 0) return null;
  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
      {items.map((it, i) => (
        <div
          key={i}
          className="rounded-lg bg-[#0f0f0f] border border-[#252525] p-2.5"
        >
          <div className={`text-[10px] uppercase tracking-[0.2em] mb-0.5 ${it.color}`}>
            {it.emoji} {it.label}
          </div>
          <div className="text-xs text-neutral-200 leading-relaxed">
            {it.text}
          </div>
        </div>
      ))}
    </div>
  );
}
